import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { ArcadeGameKind } from "@/utils/arcade/games";
import type { ArcadeTheme } from "@/utils/arcade/theme";
import { renderGameThumbnail } from "./thumbnails";

interface GameThumbnailProps {
  kind: ArcadeGameKind;
  theme: ArcadeTheme;
  coverUrl?: string;
  width: number;
  height: number;
  alt?: string;
  eager?: boolean;
}

// Paints a real frame of the game once the card is near the viewport. Until
// then (and if a canvas is not available) it shows the song's own gradient.
export function GameThumbnail({ kind, theme, coverUrl, width, height, alt = "", eager = false }: GameThumbnailProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [near, setNear] = useState(eager);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (near || !ref.current || typeof IntersectionObserver === "undefined") {
      if (typeof IntersectionObserver === "undefined") setNear(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setNear(true);
        observer.disconnect();
      }
    }, { rootMargin: "300px" });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near) return;
    const controller = new AbortController();
    renderGameThumbnail({ kind, theme, coverUrl, width, height }, controller.signal).then((next) => {
      if (!controller.signal.aborted && next) setUrl(next);
    });
    return () => controller.abort();
  }, [near, kind, theme, coverUrl, width, height]);

  const { palette } = theme;
  return (
    <Box
      ref={ref}
      sx={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(120% 80% at 70% 20%, ${palette.primary}, transparent 60%), linear-gradient(180deg, ${palette.background}, ${palette.backgroundDeep})`,
      }}
    >
      {url ? (
        <Box
          component="img"
          src={url}
          alt={alt}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            animation: "arcadeThumbIn 0.5s ease both",
            "@keyframes arcadeThumbIn": { from: { opacity: 0 }, to: { opacity: 1 } },
          }}
        />
      ) : null}
    </Box>
  );
}

export default GameThumbnail;
